import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ReviewsSection = ({ reviews, averageRating, totalReviews }) => {
  const [sortBy, setSortBy] = useState('recent');
  const [filterBy, setFilterBy] = useState('all');
  const [showAllReviews, setShowAllReviews] = useState(false);

  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'helpful', label: 'Most Helpful' },
    { value: 'highest', label: 'Highest Rating' },
    { value: 'lowest', label: 'Lowest Rating' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Reviews' },
    { value: '5', label: '5 Stars' },
    { value: '4', label: '4 Stars' },
    { value: '3', label: '3 Stars' },
    { value: '2', label: '2 Stars' },
    { value: '1', label: '1 Star' }
  ];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}
      />
    ));
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews?.forEach(review => {
      distribution[Math.floor(review.rating)]++;
    });
    return distribution;
  };

  const ratingDistribution = getRatingDistribution();
  const displayedReviews = showAllReviews ? reviews : reviews?.slice(0, 3);

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white">
      <h2 className="text-2xl font-bold text-foreground mb-6">Reviews & Ratings</h2>
      {/* Rating Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Overall Rating */}
        <div className="text-center lg:text-left">
          <div className="flex items-center justify-center lg:justify-start space-x-2 mb-2">
            <span className="text-4xl font-bold text-foreground">{averageRating}</span>
            <div className="flex items-center">
              {renderStars(averageRating)}
            </div>
          </div>
          <p className="text-muted-foreground">Based on {totalReviews} reviews</p>
        </div>

        {/* Rating Distribution */}
        <div className="space-y-2">
          {[5, 4, 3, 2, 1]?.map((rating) => (
            <div key={rating} className="flex items-center space-x-3">
              <span className="text-sm font-medium w-6">{rating}</span>
              <Icon name="Star" size={14} className="text-yellow-400 fill-current" />
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-400 h-2 rounded-full"
                  style={{
                    width: `${totalReviews > 0 ? (ratingDistribution?.[rating] / totalReviews) * 100 : 0}%`
                  }}
                />
              </div>
              <span className="text-sm text-muted-foreground w-8">
                {ratingDistribution?.[rating]}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Filters and Sorting */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Select
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
            placeholder="Sort by"
          />
        </div>
        <div className="flex-1">
          <Select
            options={filterOptions}
            value={filterBy}
            onChange={setFilterBy}
            placeholder="Filter by rating"
          />
        </div>
      </div>
      {/* Reviews List */}
      <div className="space-y-6">
        {displayedReviews?.map((review) => (
          <div key={review?.id} className="border border-border rounded-lg p-6">
            {/* Review Header */}
            <div className="flex items-start space-x-4 mb-4">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
                <Image
                  src={review?.userAvatar}
                  alt={review?.userName}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-semibold text-foreground">{review?.userName}</h4>
                  <span className="text-sm text-muted-foreground">{formatDate(review?.date)}</span>
                </div>
                <div className="flex items-center space-x-2 mb-2">
                  <div className="flex items-center">
                    {renderStars(review?.rating)}
                  </div>
                  <span className="text-sm text-muted-foreground">•</span>
                  <span className="text-sm text-muted-foreground">{review?.tripType}</span>
                </div>
              </div>
            </div>

            {/* Review Content */}
            <div className="mb-4">
              <h5 className="font-medium text-foreground mb-2">{review?.title}</h5>
              <p className="text-muted-foreground leading-relaxed">{review?.content}</p>
            </div>

            {/* Review Images */}
            {review?.images && review?.images?.length > 0 && (
              <div className="flex space-x-2 mb-4 overflow-x-auto">
                {review?.images?.map((image, index) => (
                  <div key={index} className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden">
                    <Image
                      src={image}
                      alt={`Review image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Review Categories */}
            {review?.categories && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {Object.entries(review?.categories)?.map(([category, rating]) => (
                  <div key={category} className="text-center">
                    <div className="text-sm font-medium text-foreground capitalize">{category}</div>
                    <div className="flex items-center justify-center mt-1">
                      {renderStars(rating)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Review Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-4">
                <button className="flex items-center space-x-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <Icon name="ThumbsUp" size={16} />
                  <span>Helpful ({review?.helpfulCount})</span>
                </button>
                <button className="flex items-center space-x-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <Icon name="Flag" size={16} />
                  <span>Report</span>
                </button>
              </div>
              {review?.verified && (
                <div className="flex items-center space-x-1 text-sm text-success">
                  <Icon name="Shield" size={16} />
                  <span>Verified Purchase</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Load More Button */}
      {!showAllReviews && reviews?.length > 3 && (
        <div className="text-center mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAllReviews(true)}
            iconName="ChevronDown"
            iconPosition="right"
          >
            Show All {reviews?.length} Reviews
          </Button>
        </div>
      )}
      {/* Write Review CTA */}
      <div className="mt-8 p-6 bg-muted/30 rounded-lg text-center">
        <h3 className="font-semibold text-foreground mb-2">Share Your Experience</h3>
        <p className="text-muted-foreground mb-4">
          Help other travelers by sharing your thoughts about this tour
        </p>
        <Button
          variant="outline"
          iconName="Edit"
          iconPosition="left"
        >
          Write a Review
        </Button>
      </div>
    </div>
  );
};

export default ReviewsSection;